import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiWarningLight, PiClockCountdownLight, PiRocketLaunchLight, PiXLight, PiLockLight, PiReceiptLight } from 'react-icons/pi';
import clsx from 'clsx';
import { UPGRADE_PATH } from "../../../config/entitlementKeys";
import {
  daysUntil,
  isInGrace,
  isLicensingEnforced,
  isReadOnly,
  isRenewalLocked,
  isTrialing,
} from "../../../store/actions/PlanActions";
import { formatLicenseDate } from "./renewal";

/* ─────────────────────────────────────────────────────────
   Renders only where the backend enforces licensing for this
   org (`isLicensingEnforced` - false while not loaded).
   ───────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────
   Helper: pick urgency level from days remaining
   ───────────────────────────────────────────────────────── */
type Urgency = 'critical' | 'warning' | 'info' | null;

function getUrgency(daysRemaining: number | null): Urgency {
  if (daysRemaining === null) return null;
  if (daysRemaining <= 1) return 'critical';
  if (daysRemaining <= 3) return 'critical';
  if (daysRemaining <= 7) return 'warning';
  if (daysRemaining <= 14) return 'info';
  return null;
}

const urgencyStyles: Record<string, string> = {
  critical: 'bg-danger-50 border-danger-200 text-danger-800 dark:bg-danger-900/30 dark:border-danger-800 dark:text-danger-200',
  warning: 'bg-warning-50 border-warning-200 text-warning-800 dark:bg-warning-900/30 dark:border-warning-800 dark:text-warning-200',
  info: 'bg-info-50 border-info-200 text-info-800 dark:bg-info-900/30 dark:border-info-800 dark:text-info-200',
};

const urgencyIcons: Record<string, JSX.Element> = {
  critical: <PiWarningLight className="h-4 w-4 text-danger-500 dark:text-danger-400 flex-shrink-0" />,
  warning: <PiClockCountdownLight className="h-4 w-4 text-warning-500 dark:text-warning-400 flex-shrink-0" />,
  info: <PiClockCountdownLight className="h-4 w-4 text-info-500 dark:text-info-400 flex-shrink-0" />,
};

/** "Payment due" shows from this many days out. */
const PAYMENT_DUE_WINDOW_DAYS = 14;

type BannerAction =
  /** Admin: a button. Member: the "Contact your administrator" note. */
  | { kind: 'upgrade' }
  /** Admin: a button with this label to this path. Member: nothing. */
  | { kind: 'admin-only'; label: string; path: string };

/* ─────────────────────────────────────────────────────────
   LicenseBanner - renders context-aware banners. Two are
   STICKY (not dismissible, one at a time, everyone sees them):
   A. read_only (legacy `expired` included)
   B. grace
   and below them the dismissible ones:
   1. Payment past due
   2. Trial countdown (trial_ends_at, or the legacy plan_type)
   3. Payment due within 14 days (payment_due_at)
   4. Subscription expiring soon
   ───────────────────────────────────────────────────────── */
const LicenseBanner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();

  const access = useSelector((state: RootState) => state.license?.access);
  const enforced = useSelector(isLicensingEnforced);
  const isOrgAdmin = useSelector((state: RootState) => state.user?.userInfo?.is_org_admin === true);

  // Dismissed banners (persisted in sessionStorage so they don't nag on every render)
  const [dismissed, setDismissed] = React.useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem('pons_dismissed_banners');
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  if (!enforced || !access) return null;

  const lang = i18n?.language;
  const fmt = (iso: string | null | undefined) => formatLicenseDate(iso, lang);

  /* A sticky banner: role="alert", no dismiss. Admins renew on /plans; a
     member is told who can. */
  const sticky = (testId: string, urgency: 'critical' | 'warning', icon: JSX.Element, message: string) => (
    <div className="space-y-1 px-4 pt-2">
      <div
        role="alert"
        data-testid={testId}
        className={clsx(
          'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium',
          urgencyStyles[urgency]
        )}
      >
        {icon}
        <span className="flex-1">
          {message}{' '}
          {!isOrgAdmin && t('Contact your administrator to renew it.')}
        </span>
        {isOrgAdmin && (
          <button
            className="text-xs font-medium underline hover:no-underline whitespace-nowrap"
            onClick={() => history.push(UPGRADE_PATH)}
          >
            {t('Renew license')}
          </button>
        )}
      </div>
    </div>
  );

  /*
    A. READ-ONLY COMES FIRST, AND FOR EVERYONE.

    The backend refuses every write; the request guard refuses them before they
    leave. This is the one place that says so before anyone tries. Not
    dismissible: it is true on every page until the licence is renewed.
  */
  if (isReadOnly(access)) {
    return sticky(
      'license-read-only-banner',
      'critical',
      <PiLockLight className="h-4 w-4 text-danger-500 dark:text-danger-400 flex-shrink-0" />,
      t('Read-only: you can view and export existing data, but not create or change anything.')
    );
  }

  /* A state this client does not know, flagged needs_renewal (the contract's
     transition rule): the generic "expired" banner. */
  if (isRenewalLocked(access)) {
    return sticky(
      'license-expired-banner',
      'critical',
      <PiWarningLight className="h-4 w-4 text-danger-500 dark:text-danger-400 flex-shrink-0" />,
      t('Your license has expired.')
    );
  }

  /*
    B. GRACE: full access, for now. Not dismissible - the date it stops is the
    thing everyone needs to have seen.
  */
  if (isInGrace(access)) {
    const ended = fmt(access.effective_end);
    const until = fmt(access.grace_ends_at);
    return sticky(
      'license-grace-banner',
      'warning',
      <PiWarningLight className="h-4 w-4 text-warning-500 dark:text-warning-400 flex-shrink-0" />,
      ended && until
        ? t('Your license ended on {{endDate}}. Full access continues until {{graceDate}}, then the workspace becomes read-only.', {
            endDate: ended,
            graceDate: until,
          })
        : t('Your license has ended. Full access continues for a short grace period, then the workspace becomes read-only.')
    );
  }

  const info = access.license_info;
  const banners: Array<{
    key: string;
    urgency: Urgency;
    icon: JSX.Element;
    message: string;
    action: BannerAction;
  }> = [];

  // 1. Payment past due warning
  if (info?.has_past_due_payment) {
    banners.push({
      key: 'payment-past-due',
      urgency: 'warning',
      icon: <PiWarningLight className="h-4 w-4 text-warning-500 dark:text-warning-400 flex-shrink-0" />,
      message: t('A payment has failed. Please update your payment method to avoid service interruption.'),
      action: { kind: 'upgrade' },
    });
  }

  // 2. Trial countdown - from trial_ends_at when the backend names the state,
  //    else the legacy plan_type + days_remaining.
  const trialDays = isTrialing(access) ? daysUntil(access.trial_ends_at) : null;
  const legacyTrial = !isTrialing(access) && info?.plan_type === 'trial';
  const daysRemaining = trialDays ?? (legacyTrial || isTrialing(access) ? info?.days_remaining ?? null : null);
  if ((isTrialing(access) || legacyTrial) && daysRemaining !== null) {
    const urgency = getUrgency(daysRemaining) ?? 'info';
    const dayLabel = daysRemaining === 1 ? t('day') : t('days');
    const trialEnd = fmt(access.trial_ends_at);
    banners.push({
      key: 'trial-expiry',
      urgency,
      icon: <PiRocketLaunchLight className="h-4 w-4 flex-shrink-0" />,
      message:
        daysRemaining <= 0
          ? t('Your trial has expired. Upgrade now to continue using PONS.')
          : trialEnd
          ? t('Your trial ends in {{days}} {{dayLabel}}, on {{date}}. Upgrade to keep access to all features.', {
              days: daysRemaining,
              dayLabel,
              date: trialEnd,
            })
          : t('Your trial expires in {{days}} {{dayLabel}}. Upgrade to keep access to all features.', {
              days: daysRemaining,
              dayLabel,
            }),
      action: { kind: 'upgrade' },
    });
  }

  // 3. Payment due soon (an invoice the admin or `provision` set a date on)
  const dueDays = daysUntil(access.payment_due_at);
  if (dueDays !== null && dueDays >= 0 && dueDays <= PAYMENT_DUE_WINDOW_DAYS) {
    const urgency = getUrgency(dueDays) ?? 'info';
    banners.push({
      key: `payment-due-${access.payment_due_at}`,
      urgency,
      icon: <PiReceiptLight className="h-4 w-4 flex-shrink-0" />,
      message: isOrgAdmin
        ? t('Payment due on {{date}}. Pay the invoice to keep full access.', { date: fmt(access.payment_due_at) })
        : t("Your organization's payment is due on {{date}}. Your administrator handles billing.", {
            date: fmt(access.payment_due_at),
          }),
      action: { kind: 'admin-only', label: t('Go to billing'), path: UPGRADE_PATH },
    });
  }

  // 4. Paid subscription expiring
  if (info && info.plan_type !== 'trial' && !isTrialing(access) && info.is_expiring_soon && info.days_remaining !== null) {
    const urgency = getUrgency(info.days_remaining) ?? 'warning';
    banners.push({
      key: 'subscription-expiry',
      urgency,
      icon: urgencyIcons[urgency] || urgencyIcons.warning,
      message: t('Your {{plan}} subscription expires in {{days}} days. Renew to avoid interruption.', {
        plan: info.plan_display_name || info.plan_name,
        days: info.days_remaining,
      }),
      action: { kind: 'upgrade' },
    });
  }

  // Seat warnings removed - seats are now assignment-driven with no cap.
  // Admins assign freely, billing adjusts automatically via Stripe.

  // Filter dismissed
  const visible = banners.filter((b) => !dismissed.has(b.key));
  if (!visible.length) return null;

  const handleDismiss = (key: string) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(key);
      try {
        sessionStorage.setItem('pons_dismissed_banners', JSON.stringify([...next]));
      } catch {
        /* storage unavailable - dismissed for this render tree only */
      }
      return next;
    });
  };

  return (
    <div className="space-y-1 px-4 pt-2">
      {visible.map((banner) => (
        <div
          key={banner.key}
          data-testid={`license-banner-${banner.key.startsWith('payment-due') ? 'payment-due' : banner.key}`}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium',
            urgencyStyles[banner.urgency ?? 'info']
          )}
        >
          {banner.icon}
          <span className="flex-1">{banner.message}</span>
          {/* Only an admin can act on /plans; a member is told who can. */}
          {isOrgAdmin ? (
            <button
              className="text-xs font-medium underline hover:no-underline whitespace-nowrap"
              onClick={() =>
                history.push(banner.action.kind === 'admin-only' ? banner.action.path : UPGRADE_PATH)
              }
            >
              {banner.action.kind === 'admin-only' ? banner.action.label : t('Upgrade')}
            </button>
          ) : banner.action.kind === 'upgrade' ? (
            <span className="whitespace-nowrap">{t('Contact your administrator')}</span>
          ) : null}
          <button
            className="p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/10"
            aria-label={t('Dismiss')}
            onClick={() => handleDismiss(banner.key)}
          >
            <PiXLight className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default LicenseBanner;
